import { DateTime } from "luxon";
import { buildCustomPeriod, buildPeriod, currentPeriod, isCurrentPeriod, type PeriodWindow } from "./periodWindow";

export type QuickPresetId =
  | "last2"
  | "last7"
  | "last30"
  | "last60"
  | "last90"
  | "thisMonth"
  | "lastMonth"
  | "last3Months"
  | "ytd";

export type QuickPreset = {
  id: QuickPresetId;
  label: string;
};

export const QUICK_PRESETS: QuickPreset[] = [
  { id: "last2", label: "Last 2 days" },
  { id: "last7", label: "Last 7 days" },
  { id: "last30", label: "Last 30 days" },
  { id: "last60", label: "Last 60 days" },
  { id: "last90", label: "Last 90 days" },
  { id: "thisMonth", label: "This month" },
  { id: "lastMonth", label: "Last month" },
  { id: "last3Months", label: "Last 3 months" },
  { id: "ytd", label: "Year to date" }
];

const REPORTING_SHORTCUT_KEY_BY_PRESET: Record<QuickPresetId, string> = {
  last2: "last2days",
  last7: "last7days",
  last30: "last30days",
  last60: "last60days",
  last90: "last90days",
  thisMonth: "thisMonth",
  lastMonth: "lastMonth",
  last3Months: "last3Months",
  ytd: "ytd"
};

function rollingDaysPeriod(days: number, label: string): PeriodWindow {
  const end = DateTime.now().endOf("day");
  const start = end.minus({ days: days - 1 }).startOf("day");
  return { ...buildCustomPeriod(start, end), label };
}

function periodWindowToShortcutDates(period: PeriodWindow): { start: Date; end: Date } {
  return {
    start: period.start.startOf("day").toJSDate(),
    end: period.end.startOf("day").toJSDate()
  };
}

export function buildQuickPreset(id: QuickPresetId): PeriodWindow {
  const now = DateTime.now();
  switch (id) {
    case "last2":
      return rollingDaysPeriod(2, "Last 2 days");
    case "last7":
      return rollingDaysPeriod(7, "Last 7 days");
    case "last30":
      return rollingDaysPeriod(30, "Last 30 days");
    case "last60":
      return rollingDaysPeriod(60, "Last 60 days");
    case "last90":
      return rollingDaysPeriod(90, "Last 90 days");
    case "thisMonth":
      return currentPeriod("month");
    case "lastMonth":
      return buildPeriod("month", now.minus({ months: 1 }));
    case "last3Months": {
      const end = now.endOf("day");
      const start = end.minus({ months: 3 }).plus({ days: 1 }).startOf("day");
      return { ...buildCustomPeriod(start, end), label: "Last 3 months" };
    }
    case "ytd":
      return currentPeriod("ytd");
  }
}

function sameCalendarDay(a: DateTime, b: DateTime): boolean {
  return a.hasSame(b, "day");
}

export function periodMatchesQuickPreset(period: PeriodWindow, id: QuickPresetId): boolean {
  const preset = buildQuickPreset(id);
  if (id === "thisMonth") {
    return period.mode === "month" && isCurrentPeriod(period);
  }
  if (id === "lastMonth") {
    return period.mode === "month" && period.anchor.hasSame(DateTime.now().minus({ months: 1 }), "month");
  }
  if (id === "ytd") {
    return period.mode === "ytd" && isCurrentPeriod(period);
  }
  return (
    period.mode === "custom" && sameCalendarDay(period.start, preset.start) && sameCalendarDay(period.end, preset.end)
  );
}

export function getReportingDateRangeShortcuts() {
  const shortcuts: Record<string, { text: string; period: { start: Date; end: Date } }> = {};

  for (const preset of QUICK_PRESETS) {
    const window = buildQuickPreset(preset.id);
    shortcuts[REPORTING_SHORTCUT_KEY_BY_PRESET[preset.id]] = {
      text: preset.label,
      period: periodWindowToShortcutDates(window)
    };
  }

  return shortcuts;
}

export function findQuickPresetIdForRange(start: DateTime, end: DateTime): QuickPresetId | null {
  for (const preset of QUICK_PRESETS) {
    const built = buildQuickPreset(preset.id);
    if (sameCalendarDay(start, built.start) && sameCalendarDay(end, built.end)) {
      return preset.id;
    }
  }
  return null;
}

/** Keep `mode: custom` while applying shortcut/picker dates (and preset label when recognized). */
export function buildCustomPeriodForPickerRange(start: DateTime, end: DateTime): PeriodWindow {
  const period = buildCustomPeriod(start, end);
  const presetId = findQuickPresetIdForRange(start, end);
  if (!presetId) return period;

  const preset = QUICK_PRESETS.find((entry) => entry.id === presetId);
  return preset ? { ...period, label: preset.label } : period;
}

const ROLLING_QUICK_PRESET_IDS = ["last2", "last7", "last30", "last60", "last90", "last3Months"] as const;

export function isRollingQuickPresetPeriod(period: PeriodWindow): boolean {
  return ROLLING_QUICK_PRESET_IDS.some((id) => periodMatchesQuickPreset(period, id));
}
